import React, { useCallback, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { DateRange, Granularity } from '../../types/Dashboard';
import { today, addMonths, addDays, startOfMonth, formatDateAR, bucketize } from './dashboardHelpers';
import { Card, Segmented, SegmentedOption, easeStandard } from '../ui';
import { AlertIcon } from '../icons';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Props {
  range: DateRange;
  granularity: Granularity;
  onRangeChange: (range: DateRange) => void;
  onGranularityChange: (g: Granularity) => void;
  maxMonthsBack?: number; // default 24
}

// ---------------------------------------------------------------------------
// Preset helpers
// ---------------------------------------------------------------------------

interface Preset {
  label: string;
  title: string;
  getRange: () => DateRange;
}

function buildPresets(): Preset[] {
  return [
    {
      label: '7d',
      title: 'Últimos 7 días',
      getRange: () => ({ from: addDays(today(), -6), to: today() }),
    },
    {
      label: '30d',
      title: 'Últimos 30 días',
      getRange: () => ({ from: addDays(today(), -29), to: today() }),
    },
    {
      label: '3m',
      title: 'Últimos 3 meses',
      getRange: () => ({ from: addMonths(today(), -3), to: today() }),
    },
    {
      label: '6m',
      title: 'Últimos 6 meses',
      getRange: () => ({ from: addMonths(today(), -6), to: today() }),
    },
    {
      label: 'YTD',
      title: 'Desde el 1 de enero',
      getRange: () => ({ from: startOfMonth(`${today().slice(0, 4)}-01-01`), to: today() }),
    },
    {
      label: '1a',
      title: 'Último año',
      getRange: () => ({ from: addMonths(today(), -12), to: today() }),
    },
  ];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const GRAN_LABELS: Record<Granularity, string> = {
  day: 'Día',
  week: 'Semana',
  month: 'Mes',
};

/** Plural correcto para la línea de resumen: "7 meses", no "7 meses" mal armado. */
const GRAN_PLURAL: Record<Granularity, [string, string]> = {
  day: ['día', 'días'],
  week: ['semana', 'semanas'],
  month: ['mes', 'meses'],
};

const GRANULARITY_OPTIONS: SegmentedOption<Granularity>[] = [
  { value: 'day', label: GRAN_LABELS.day, title: 'Un punto por día' },
  { value: 'week', label: GRAN_LABELS.week, title: 'Un punto por semana ISO' },
  { value: 'month', label: GRAN_LABELS.month, title: 'Un punto por mes' },
];

const groupLabel = 'mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-400';

const dateInput =
  'rounded-lg border border-slate-300 px-3 py-1.5 text-sm tabular-nums text-slate-700 ' +
  'transition-colors duration-150 ease-out outline-none ' +
  'focus:border-gappex-primary focus:ring-2 focus:ring-gappex-primary/30';

export default function DashboardFilters({
  range,
  granularity,
  onRangeChange,
  onGranularityChange,
  maxMonthsBack = 24,
}: Props) {
  const presets = useMemo(buildPresets, []);

  const minAllowed = addMonths(today(), -maxMonthsBack);

  const [warning, setWarning] = React.useState<string | null>(null);

  const applyRange = useCallback(
    (next: DateRange) => {
      let { from } = next;
      const { to } = next;
      let warn: string | null = null;

      if (from < minAllowed) {
        from = minAllowed;
        warn = `El rango máximo es de ${maxMonthsBack} meses. La fecha de inicio se ajustó al ${formatDateAR(minAllowed)}.`;
      }

      if (from > to) {
        setWarning('La fecha de inicio no puede ser posterior a la de fin.');
        return;
      }

      setWarning(warn);
      onRangeChange({ from, to });
    },
    [minAllowed, maxMonthsBack, onRangeChange]
  );

  /**
   * Qué preset está activo hoy. Sin esto los atajos no tienen estado visible y
   * el usuario no sabe qué recorte está mirando.
   */
  const activePreset = useMemo(() => {
    const match = presets.find((p) => {
      const r = p.getRange();
      return r.from === range.from && r.to === range.to;
    });
    return match?.label ?? '';
  }, [presets, range]);

  const handlePreset = (label: string) => {
    const preset = presets.find((p) => p.label === label);
    if (!preset) return;
    setWarning(null);
    applyRange(preset.getRange());
  };

  const handleFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    applyRange({ from: e.target.value, to: range.to });
  };

  const handleToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    applyRange({ from: range.from, to: e.target.value });
  };

  const buckets = bucketize(range, granularity);
  const [uno, varios] = GRAN_PLURAL[granularity];
  const summary =
    `${formatDateAR(range.from)} → ${formatDateAR(range.to)} · ` +
    `${buckets.length} ${buckets.length === 1 ? uno : varios}`;

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
        <div>
          <span className={groupLabel}>Rango</span>
          <Segmented
            options={presets.map((p) => ({ value: p.label, label: p.label, title: p.title }))}
            value={activePreset}
            onChange={handlePreset}
            ariaLabel="Rango rápido"
          />
        </div>

        <div className="flex items-end gap-3">
          <div>
            <label className={groupLabel} htmlFor="df-from">
              Desde
            </label>
            <input
              id="df-from"
              type="date"
              value={range.from}
              min={minAllowed}
              max={range.to}
              onChange={handleFromChange}
              className={dateInput}
            />
          </div>
          <div>
            <label className={groupLabel} htmlFor="df-to">
              Hasta
            </label>
            <input
              id="df-to"
              type="date"
              value={range.to}
              min={range.from}
              onChange={handleToChange}
              className={dateInput}
            />
          </div>
        </div>

        <div className="sm:ms-auto">
          <span className={groupLabel}>Detalle</span>
          <Segmented
            options={GRANULARITY_OPTIONS}
            value={granularity}
            onChange={onGranularityChange}
            ariaLabel="Granularidad de los gráficos"
          />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
        <p className="text-xs tabular-nums text-slate-400">{summary}</p>

        <AnimatePresence initial={false}>
          {warning && (
            <motion.p
              key="warn"
              role="status"
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0, transition: { duration: 0.25, ease: easeStandard } }}
              exit={{ opacity: 0, y: -6, transition: { duration: 0.15, ease: 'easeOut' } }}
              className="flex items-center gap-1.5 text-xs font-medium text-amber-600"
            >
              <AlertIcon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
              {warning}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </Card>
  );
}
